import { useEffect, useState, useCallback, useRef } from "react";
import { getAuthorized, postAuthorized, deleteAuthorized } from "../../../hooks/User";

const STATUS_LABELS = {
    pending: "В очереди",
    processing: "Обработка...",
    processed: "Готово",
    failed: "Ошибка",
};

const FIELD_LABELS = {
    document_date: "Дата документа",
    diagnosis: "Диагнозы / заболевания",
    doctor_name: "Врач",
    doctor: "Врач",
    institution: "Учреждение",
};

function formatDate(value) {
    if (!value) return null;
    const d = new Date(value);
    if (isNaN(d)) return value;
    return d.toLocaleDateString("ru-RU");
}

function FieldValue({ field, value }) {
    if (value === null || value === undefined || value === "") return null;
    if (field === "diagnosis" && Array.isArray(value)) {
        return (
            <ul className="md-diagnosis-list">
                {value.map((item, idx) => (
                    <li key={idx}>{item}</li>
                ))}
            </ul>
        );
    }
    if ((field === "doctor" || field === "doctor_name") && Array.isArray(value)) {
        return <span>{value.join(", ")}</span>;
    }
    if (field === "document_date") return <span>{formatDate(value)}</span>;
    return <span>{value}</span>;
}

function DocumentDetails({ extractedData }) {
    if (!extractedData || Object.keys(extractedData).length === 0) {
        return <div style={{ fontSize: "0.85rem", color: "#999", marginTop: "6px" }}>Нет данных</div>;
    }

    return (
        <div className="md-doc-details">
            {Object.entries(extractedData).map(([field, value]) => {
                if (value === null || value === undefined || value === "") return null;
                if (Array.isArray(value) && value.length === 0) return null;
                return (
                    <div key={field} className="md-doc-field">
                        <span className="md-doc-field-label">{FIELD_LABELS[field] || field}:</span>
                        <FieldValue field={field} value={value} />
                    </div>
                );
            })}
        </div>
    );
}

const ACCEPTED_TYPES = [
    "application/pdf",
    "image/png",
    "image/jpeg",
    "image/jpg",
    "text/plain",
    "text/markdown",
    "text/csv",
    "application/json",
    "text/xml",
    "text/html",
    "text/yaml",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const ACCEPTED_EXTENSIONS = ".pdf,.png,.jpg,.jpeg,.txt,.md,.docx,.csv,.json,.xml,.html,.htm,.log,.yaml,.yml";

export default function MedicalDocumentsSection({ isDoctor }) {
    const [isOpen, setIsOpen] = useState(false);
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState("");
    const [isDragOver, setIsDragOver] = useState(false);
    const fileInputRef = useRef(null);
    const pollingRef = useRef(null);

    const prefix = isDoctor ? "/doctor" : "/client";
    const uploadEndpoint = `${prefix}/upload_medical_document`;
    const listEndpoint = `${prefix}/my_medical_documents`;

    const fetchDocuments = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const response = await getAuthorized(listEndpoint);
            const items = response.data?.items || response.data || [];
            setDocuments(items);
        } catch (err) {
            setError(err.response?.data?.error_description || err.message || "Не удалось загрузить документы");
        } finally {
            setLoading(false);
        }
    }, [listEndpoint]);

    useEffect(() => {
        if (isOpen) {
            fetchDocuments();
        }
    }, [isOpen, fetchDocuments]);

    useEffect(() => {
        const hasProcessing = documents.some((d) => d.status === "processing" || d.status === "pending");
        if (isOpen && hasProcessing && !pollingRef.current) {
            pollingRef.current = setInterval(() => {
                fetchDocuments();
            }, 5000);
        } else if (!hasProcessing && pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
        return () => {
            if (pollingRef.current) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
            }
        };
    }, [documents, fetchDocuments, isOpen]);

    const isAcceptedType = (file) => {
        if (ACCEPTED_TYPES.includes(file.type)) return true;
        const name = file.name.toLowerCase();
        return ACCEPTED_EXTENSIONS.split(",").some((ext) => name.endsWith(ext.trim()));
    };

    const uploadFile = async (file) => {
        if (!file) return;

        if (!isAcceptedType(file)) {
            setError("Поддерживаются PDF, изображения, Word (.docx), текст, markdown, CSV, JSON, XML, HTML, YAML и лог-файлы");
            return;
        }

        const form = new FormData();
        form.append("file", file);

        setUploading(true);
        setError("");
        try {
            const response = await postAuthorized(uploadEndpoint, form, {});
            const newDoc = response.data;
            if (newDoc) {
                setDocuments((prev) => [newDoc, ...prev]);
            }
            await fetchDocuments();
        } catch (err) {
            setError(err.response?.data?.error_description || err.message || "Не удалось загрузить документ");
        } finally {
            setUploading(false);
        }
    };

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (file) await uploadFile(file);
        e.target.value = "";
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        setIsDragOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file) await uploadFile(file);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Удалить документ?")) return;
        try {
            await deleteAuthorized(`/medical_document/${id}`);
            setDocuments((prev) => prev.filter((d) => d.id !== id));
        } catch (err) {
            setError(err.response?.data?.error_description || err.message || "Не удалось удалить документ");
        }
    };

    const handleClose = () => {
        setIsOpen(false);
        setError("");
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            handleClose();
        }
    };

    return (
        <div className="editable-field medical-documents-section">
            <div className="field-header">
                <span className="field-label">Медицинские документы</span>
                <button className="field-edit-btn" onClick={() => setIsOpen(true)}>
                    <span className="btn-text">Просмотреть файлы</span>
                </button>
            </div>

            {isOpen && (
                <div className="modal-overlay" onClick={handleOverlayClick}>
                    <div className="modal-content medical-documents-modal" style={{ maxWidth: 560, width: "100%" }}>
                        <button className="modal-close" onClick={handleClose} aria-label="Закрыть">
                            ✕
                        </button>
                        <h2 style={{ margin: "0 0 16px 0", fontSize: "1.25rem" }}>Медицинские документы</h2>

                        <div
                            className="medical-documents-upload-zone"
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                            style={{
                                border: `2px dashed ${isDragOver ? "#4a90e2" : "#ccc"}`,
                                borderRadius: "12px",
                                padding: "24px",
                                textAlign: "center",
                                cursor: "pointer",
                                backgroundColor: isDragOver ? "rgba(74, 144, 226, 0.08)" : "#fafafa",
                                marginBottom: "16px",
                                transition: "all 0.2s ease",
                            }}
                        >
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept={ACCEPTED_EXTENSIONS}
                                onChange={handleFileChange}
                                disabled={uploading}
                                style={{ display: "none" }}
                            />
                            <div style={{ fontSize: "2rem", marginBottom: "8px" }}>📄</div>
                            <div style={{ fontWeight: 500, marginBottom: "4px" }}>
                                {uploading ? "Загрузка документа..." : "Загрузить медицинский документ"}
                            </div>
                            <div style={{ fontSize: "0.85rem", color: "#666" }}>
                                Перетащите файл сюда или нажмите для выбора
                            </div>
                            <div style={{ fontSize: "0.75rem", color: "#999", marginTop: "8px" }}>
                                PDF, PNG, JPEG, Word, TXT, MD, CSV, JSON, XML, HTML, YAML, LOG
                            </div>
                        </div>

                        {error && <p className="field-error" style={{ marginBottom: "12px" }}>{error}</p>}

                        {loading ? (
                            <p>Загрузка списка...</p>
                        ) : documents.length === 0 ? (
                            <p className="field-empty">Нет загруженных документов</p>
                        ) : (
                            <ul className="medical-documents-list" style={{ listStyle: "none", padding: 0, margin: 0, maxHeight: 360, overflowY: "auto" }}>
                                {documents.map((doc) => (
                                    <li key={doc.id} className="medical-document-card" style={{ marginBottom: "12px", padding: "12px", border: "1px solid #eee", borderRadius: "8px" }}>
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "8px" }}>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontWeight: 600 }}>
                                                    {doc.original_filename || "Документ"}
                                                </div>
                                                <DocumentDetails extractedData={doc.extracted_data} />
                                                {doc.creation_dt && (
                                                    <div style={{ fontSize: "0.75rem", color: "#999", marginTop: "6px" }}>
                                                        Загружен: {new Date(doc.creation_dt).toLocaleString("ru-RU")}
                                                    </div>
                                                )}
                                                <div style={{ fontSize: "0.8rem", color: doc.status === "failed" ? "#d32f2f" : "#888", marginTop: "4px" }}>
                                                    {doc.status === "processing" || doc.status === "pending" ? (
                                                        <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                                                            <span className="spinner" style={{
                                                                display: "inline-block",
                                                                width: "12px",
                                                                height: "12px",
                                                                border: "2px solid #ccc",
                                                                borderTopColor: "#4a90e2",
                                                                borderRadius: "50%",
                                                                animation: "spin 1s linear infinite",
                                                            }} />
                                                            {STATUS_LABELS[doc.status] || doc.status}
                                                        </span>
                                                    ) : (
                                                        STATUS_LABELS[doc.status] || doc.status
                                                    )}
                                                </div>
                                                {doc.status === "failed" && doc.processing_error && (
                                                    <div style={{ fontSize: "0.75rem", color: "#d32f2f", marginTop: "4px" }}>
                                                        {doc.processing_error}
                                                    </div>
                                                )}
                                            </div>
                                            <button
                                                className="field-unlink-btn"
                                                onClick={() => handleDelete(doc.id)}
                                                title="Удалить"
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
